import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Plus, Trash2, FileDown, Mail, ExternalLink, ShoppingCart, ClipboardCheck, PackageCheck, MailCheck,
} from "lucide-react";
import { InkoopStatusBadge } from "@/lib/inkoopStatus";
import { InboundSectie } from "@/pages/InstellingenChannelDock";
import { toast } from "sonner";
import { useDetailNav } from "@/lib/overzichtNav";
import { OverzichtNav } from "@/components/OverzichtNav";

// Artikelen beheren in één pop-up: producten aanvinken, daarna aantallen/inkoopprijzen aanpassen.
function ArtikelenInkoopDialog({ producten, regels, onSave, trigger }) {
  const [open, setOpen] = useState(false);
  const [zoek, setZoek] = useState("");
  const [sel, setSel] = useState({}); // product_id -> regel
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const init = {};
    (regels || []).forEach(r => {
      const key = r.product_id || r.code;
      init[key] = {
        product_id: r.product_id, code: r.code, naam: r.naam,
        aantal: r.aantal, inkoopprijs: r.inkoopprijs, eenheid: r.eenheid || "stuk",
        artikelcode_leverancier: r.artikelcode_leverancier || "",
      };
    });
    setSel(init); setZoek("");
  }, [open]); // eslint-disable-line

  const toggle = (p) => setSel(prev => {
    const n = { ...prev };
    if (n[p.id]) { delete n[p.id]; return n; }
    n[p.id] = {
      product_id: p.id, code: p.sku, naam: p.naam, aantal: 1,
      inkoopprijs: Number(p.inkoopprijs || 0), eenheid: p.eenheid || "stuk",
      artikelcode_leverancier: p.artikelcode_leverancier || "",
    };
    return n;
  });
  const upd = (key, veld, val) => setSel(prev => ({ ...prev, [key]: { ...prev[key], [veld]: val } }));

  const gefilterd = useMemo(() => {
    const t = zoek.trim().toLowerCase();
    const base = producten.filter(p => !t
      || (p.naam || "").toLowerCase().includes(t)
      || (p.sku || "").toLowerCase().includes(t)
      || (p.artikelcode_leverancier || "").toLowerCase().includes(t));
    return base.slice(0, 60);
  }, [zoek, producten]);

  const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
  const gekozen = Object.values(sel);
  const totaal = gekozen.reduce((s, r) => s + num(r.aantal) * num(r.inkoopprijs), 0);

  const opslaan = async () => {
    setBusy(true);
    try {
      const next = gekozen.map(r => ({
        product_id: r.product_id, code: r.code, naam: r.naam,
        aantal: num(r.aantal), inkoopprijs: num(r.inkoopprijs),
        eenheid: r.eenheid || "stuk", artikelcode_leverancier: r.artikelcode_leverancier || "",
      }));
      await onSave(next);
      setOpen(false);
    } catch { toast.error("Opslaan mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-5xl w-[95vw]" data-testid="artikelen-inkoop-dialog">
        <DialogHeader><DialogTitle>Artikelen beheren</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Links: aanvinkbare productenlijst */}
          <div>
            <Input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek product op naam, SKU of artikelcode…"
              data-testid="artikel-inkoop-zoek" autoComplete="off" />
            <div className="mt-2 border border-border rounded-lg max-h-[52vh] overflow-y-auto divide-y divide-border">
              {gefilterd.map(p => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-secondary cursor-pointer text-sm"
                  data-testid={`artikel-inkoop-optie-${p.sku}`}>
                  <Checkbox checked={!!sel[p.id]} onCheckedChange={() => toggle(p)} />
                  <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">{p.sku}</span>
                  <span className="flex-1 leading-snug">{p.naam}</span>
                  <span className="text-xs shrink-0 text-right text-muted-foreground">{fmtEUR(p.inkoopprijs)}</span>
                </label>
              ))}
              {gefilterd.length === 0 && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Geen artikelen gevonden</div>}
            </div>
          </div>
          {/* Rechts: gekozen met aantal + inkoopprijs */}
          <div>
            <div className="text-xs text-muted-foreground mb-2">{gekozen.length} geselecteerd</div>
            <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1">
              {gekozen.length === 0 && <div className="text-sm text-muted-foreground italic">Vink links artikelen aan.</div>}
              {gekozen.map(r => {
                const key = r.product_id || r.code;
                return (
                <div key={key} className="border border-border rounded-lg p-2.5 space-y-1.5" data-testid={`artikel-inkoop-gekozen-${r.code}`}>
                  <div className="text-sm font-medium leading-snug">{r.naam}</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Aantal</Label>
                      <Input type="number" min="0" step="1" value={r.aantal} className="h-8"
                        onChange={(e) => upd(key, "aantal", e.target.value)} data-testid={`artikel-inkoop-aantal-${r.code}`} />
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Inkoop (€)</Label>
                      <Input type="number" min="0" step="0.01" value={r.inkoopprijs} className="h-8 text-right"
                        onChange={(e) => upd(key, "inkoopprijs", e.target.value)} data-testid={`artikel-inkoop-prijs-${r.code}`} />
                    </div>
                    <div className="text-right w-20">
                      <Label className="text-[10px] text-muted-foreground">Regel</Label>
                      <div className="h-8 flex items-center justify-end text-sm tabular-nums">{fmtEUR(num(r.aantal) * num(r.inkoopprijs))}</div>
                    </div>
                  </div>
                </div>
              );})}
            </div>
          </div>
        </div>
        <DialogFooter className="items-center">
          <div className="mr-auto text-sm font-medium" data-testid="artikelen-inkoop-totaal">Totaal: {fmtEUR(totaal)}</div>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={opslaan} disabled={busy} data-testid="artikelen-inkoop-opslaan">{busy ? "Bezig…" : "Opslaan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InkoopOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const nav = useDetailNav("inkooporders", id, "/inkooporders");
  const [o, setO] = useState(null);
  const [producten, setProducten] = useState([]);
  const [inboekOpen, setInboekOpen] = useState(false);
  const [inboekRegels, setInboekRegels] = useState([]);
  const [inboekBusy, setInboekBusy] = useState(false);
  const [inboekBestemming, setInboekBestemming] = useState("leverancier");
  const [inboekNotitie, setInboekNotitie] = useState("");
  const [restantPlan, setRestantPlan] = useState([]);
  const [locaties, setLocaties] = useState([]);
  const [vrijAdres, setVrijAdres] = useState("");

  const load = async () => {
    try {
      const [{ data }, { data: pr }, { data: loc }] = await Promise.all([
        api.get(`/inkooporders/${id}`), api.get("/producten"), api.get("/instellingen/locaties"),
      ]);
      setO(data); setProducten(pr); setLocaties(loc.items || []);
      setVrijAdres(data.leveradres_vrij || "");
    } catch { toast.error("Niet gevonden"); navigate("/inkooporders"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { setO(null); }, [id]);

  const prodMap = useMemo(() => Object.fromEntries(producten.map(p => [p.id, p])), [producten]);

  const patch = async (field, value) => {
    try {
      const { data } = await api.patch(`/inkooporders/${id}`, { [field]: value });
      setO(prev => ({ ...prev, ...data, leverancier: prev?.leverancier }));
      toast.success("Opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const patchMulti = async (fields) => {
    try {
      const { data } = await api.patch(`/inkooporders/${id}`, fields);
      setO(prev => ({ ...prev, ...data, leverancier: prev?.leverancier }));
      toast.success("Opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const setRegels = async (next) => {
    await api.patch(`/inkooporders/${id}`, { regels: next });
    load();
  };

  const updRegel = (idx, field, val) => {
    const next = [...(o.regels || [])];
    next[idx] = { ...next[idx], [field]: field === "aantal" || field === "inkoopprijs" ? Number(val) : val };
    setRegels(next);
  };
  const delRegel = (idx) => setRegels((o.regels || []).filter((_, i) => i !== idx));

  const verstuur = async () => {
    if (!o.leverancier?.email) { toast.error("Leverancier heeft geen e-mailadres"); return; }
    if (!(o.regels || []).length) { toast.error("Voeg eerst regels toe"); return; }
    try {
      const { data } = await api.post(`/inkooporders/${id}/verstuur`, {});
      toast.success(`Order verstuurd naar ${data.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versturen mislukt");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/inkooporders/${id}`);
      toast.success("Inkooporder verwijderd"); navigate("/inkooporders");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const openInboek = () => {
    setInboekRegels((o.regels || []).map((r) => {
      const besteld = Number(r.aantal) || 0;
      const alOntvangen = Number(r.ontvangen_totaal ?? r.ontvangen ?? 0) || 0;
      const open = Math.max(0, besteld - alOntvangen);
      return { naam: r.naam, code: r.code, product_id: r.product_id, besteld, alOntvangen, ontvangen: open };
    }));
    setInboekNotitie("");
    const openTotaal = (o.regels || []).reduce((s, r) => {
      const besteld = Number(r.aantal) || 0;
      const al = Number(r.ontvangen_totaal ?? r.ontvangen ?? 0) || 0;
      return s + Math.max(0, besteld - al);
    }, 0);
    setRestantPlan([{ aantal: openTotaal, datum: "", auto: true }]);
    setInboekOpen(true);
  };
  const syncRestant = (regels) => {
    const restant = regels.reduce(
      (s, r) => s + Math.max(0, (r.besteld || 0) - (r.alOntvangen || 0) - (Number(r.ontvangen) || 0)), 0);
    setRestantPlan((prev) => {
      if (!prev.length || !prev[0].auto) return prev;
      const overig = prev.slice(1).reduce((s, r) => s + (Number(r.aantal) || 0), 0);
      return [{ ...prev[0], aantal: Math.max(0, restant - overig) }, ...prev.slice(1)];
    });
  };
  const setOntvangen = (idx, val) => setInboekRegels((prev) => {
    const next = [...prev];
    const max = Math.max(0, (next[idx].besteld || 0) - (next[idx].alOntvangen || 0));
    next[idx] = { ...next[idx], ontvangen: Math.min(max, Math.max(0, parseInt(val, 10) || 0)) };
    syncRestant(next);
    return next;
  });
  const restantOpen = () => inboekRegels.reduce(
    (s, r) => s + Math.max(0, (r.besteld || 0) - (r.alOntvangen || 0) - (Number(r.ontvangen) || 0)), 0);
  // Regel 0 volgt automatisch het restant zolang de gebruiker die niet zelf heeft aangepast.
  const updatePlan = (next) => {
    if (next.length && next[0].auto) {
      const overig = next.slice(1).reduce((s, r) => s + (Number(r.aantal) || 0), 0);
      next = [{ ...next[0], aantal: Math.max(0, restantOpen() - overig) }, ...next.slice(1)];
    }
    setRestantPlan(next);
  };
  const bevestigInboek = async () => {
    setInboekBusy(true);
    try {
      const payload = {
        regels: inboekRegels.map((r, idx) => ({ index: idx, ontvangen: r.ontvangen })),
        bestemming: inboekBestemming,
        notitie: inboekNotitie,
        restant_planning: restantPlan
          .filter((r) => Number(r.aantal) > 0 && r.datum)
          .map((r) => ({ aantal: Number(r.aantal), datum: r.datum })),
      };
      const { data } = await api.post(`/inkooporders/${id}/inboeken`, payload);
      toast.success(data.volledig_ontvangen
        ? `Volledig ingeboekt bij ${data.bestemming === "warehouse" ? "warehouse" : "leverancier"} — ${data.bijgewerkte_producten} product(en) bijgewerkt`
        : `Deellevering ingeboekt — nog ${data.open_stuks} stuk(s) open${data.nieuwe_leverdatum ? `, nieuwe verwachte datum ${data.nieuwe_leverdatum}` : ""}`);
      setInboekOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inboeken mislukt");
    } finally { setInboekBusy(false); }
  };

  if (!o) return <div className="text-muted-foreground">Laden…</div>;
  const totaal = (o.regels || []).reduce((s, r) => s + (Number(r.aantal)||0) * (Number(r.inkoopprijs)||0), 0);
  const verstuurd = o.status === "verstuurd";
  const ingeboekt = o.volledig_ontvangen || o.status === "ingeboekt";

  return (
    <div className="space-y-6" data-testid="inkoop-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(nav.terug || "/inkooporders")} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Overzicht
          </Button>
          <OverzichtNav nav={nav} basePad="/inkooporders" testid="inkoop-nav" />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm"
             onClick={() => downloadFile(`/inkooporders/${id}/excel`, `inkooporder-${o.nummer || id.slice(0,8)}.xlsx`)}
             data-testid="excel-link">
            <FileDown className="h-4 w-4 mr-1" /> Download Excel
          </Button>
          {!verstuurd && (
            <Button onClick={verstuur} data-testid="verstuur-btn">
              <Mail className="h-4 w-4 mr-1" /> Verstuur per mail
            </Button>
          )}
          {!ingeboekt && o.inboeken_nodig !== false && (o.regels || []).length > 0 && (
            <Button variant="secondary" onClick={openInboek} data-testid="inboeken-btn">
              <ClipboardCheck className="h-4 w-4 mr-1" /> {o.status === "deels_ingeboekt" ? "Deellevering inboeken" : "Inboeken / Ontvangen"}
            </Button>
          )}
          {o.inboeken_nodig === false && (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800"
              data-testid="geen-inboeking-badge">
              Levering op {o.leveradres?.label || "extern adres"} — niet inboeken
            </span>
          )}
          {o.naar_channeldock && (
            <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-800"
              data-testid="cd-inbound-badge">
              Aangemeld bij ChannelDock — voorraad wordt daar ingeboekt
            </span>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid="verwijder-btn">
                <Trash2 className="h-4 w-4 mr-1" />Verwijderen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="verwijder-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Inkooporder verwijderen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Weet je zeker dat je inkooporder {o.nummer} definitief wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="verwijder-annuleer">Annuleren</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="verwijder-bevestig">
                  Verwijderen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-xl p-5 space-y-3 h-fit">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <ShoppingCart className="h-4 w-4 text-primary" />
            <InkoopStatusBadge status={o.status} />
            {o.dagen_te_laat > 0 && (
              <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700"
                data-testid="inkoop-te-laat-badge">
                {o.dagen_te_laat} dag{o.dagen_te_laat === 1 ? "" : "en"} te laat
              </span>
            )}
          </div>
          {(o.verwachte_leveringen || []).length > 0 && (
            <div className="text-xs text-muted-foreground" data-testid="verwachte-leveringen">
              Verwacht restant: {o.verwachte_leveringen.map((v, i) => (
                <span key={i} className="text-foreground font-medium">
                  {i > 0 ? " · " : " "}{v.aantal} stuks op {fmtDate(v.datum)}
                </span>
              ))}
            </div>
          )}
          {(o.besteld_stuks > 0) && (
            <div className="text-xs text-muted-foreground" data-testid="inkoop-ontvangst-stand">
              Ontvangen: <span className="font-medium text-foreground">{o.ontvangen_stuks}</span> van {o.besteld_stuks} stuks
              {o.open_stuks > 0 ? ` — nog ${o.open_stuks} open` : " — volledig"}
            </div>
          )}
          <div>
            <Label className="text-xs text-muted-foreground">Ordernummer</Label>
            <Input defaultValue={o.nummer || ""} onBlur={(e)=>{ if (e.target.value !== o.nummer) patch("nummer", e.target.value); }} data-testid="order-nummer" />
          </div>
          {o.leverancier ? (
            <div className="bg-secondary/40 rounded-md p-3 space-y-1 text-sm">
              <div className="font-medium">{o.leverancier.naam}</div>
              {o.leverancier.contactpersoon && <div className="text-xs text-muted-foreground">T.a.v. {o.leverancier.contactpersoon}</div>}
              {o.leverancier.email && <div><a href={`mailto:${o.leverancier.email}`} className="text-primary hover:underline text-xs">{o.leverancier.email}</a></div>}
              {o.leverancier.telefoon && <div className="text-xs">{o.leverancier.telefoon}</div>}
              <Link to={`/leveranciers/${o.leverancier.id}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-2">
                Beheer leverancier <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          ) : <div className="text-sm text-muted-foreground italic">Geen leverancier gekoppeld</div>}
          <div>
            <Label className="text-xs text-muted-foreground">Notitie</Label>
            <Textarea defaultValue={o.notitie || ""} rows={3}
              onBlur={(e)=>{ if (e.target.value !== (o.notitie || "")) patch("notitie", e.target.value); }} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Gewenste leverdatum</Label>
            <Input type="date" key={o.leverdatum || ""} defaultValue={(o.leverdatum || "").slice(0, 10)} data-testid="order-leverdatum"
              onChange={(e)=>{ if ((e.target.value || "") !== (o.leverdatum || "").slice(0,10)) patch("leverdatum", e.target.value); }} />
            <p className="text-[11px] text-muted-foreground mt-1">Wordt vermeld in de inkoopmail en op de Excel.</p>
          </div>
          <div data-testid="order-leveradres">
            <Label className="text-xs text-muted-foreground">Leveradres</Label>
            <select
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={o.leveradres?.type === "locatie" ? `locatie:${o.leveradres.locatie_id}` : (o.leveradres?.type || "bedrijf")}
              onChange={(e) => {
                const v = e.target.value;
                if (v.startsWith("locatie:")) patchMulti({ leveradres_type: "locatie", locatie_id: v.slice(8) });
                else patchMulti({ leveradres_type: v, locatie_id: null });
              }}
              data-testid="leveradres-keuze">
              {locaties.map(l => (
                <option key={l.id} value={l.id === "bedrijf" ? "bedrijf" : `locatie:${l.id}`}>
                  {l.id === "bedrijf" ? `Bedrijfsadres — ${l.naam}` : `Eigen locatie — ${l.naam}`}
                </option>
              ))}
              <option value="klant">Klantadres (dropship)</option>
              <option value="vrij">Ander adres</option>
            </select>
            {o.leveradres?.type === "vrij" && (
              <Textarea className="mt-2" rows={4} value={vrijAdres} placeholder={"Naam\nStraat 1\n1234 AB Plaats\nNederland"}
                onChange={(e) => setVrijAdres(e.target.value)}
                onBlur={() => { if (vrijAdres !== (o.leveradres_vrij || "")) patch("leveradres_vrij", vrijAdres); }}
                data-testid="leveradres-vrij" />
            )}
            {o.leveradres?.adres && o.leveradres?.type !== "vrij" && (
              <pre className="mt-2 whitespace-pre-wrap font-sans text-xs bg-secondary/40 rounded-md p-2" data-testid="leveradres-tekst">{o.leveradres.adres}</pre>
            )}
            <p className="text-[11px] mt-1 text-muted-foreground" data-testid="leveradres-hint">
              {o.inboeken_nodig
                ? "Eigen locatie: deze levering wordt ingeboekt in je voorraad."
                : "Externe levering: raakt je voorraad niet, hoeft niet ingeboekt te worden en wordt automatisch afgerond op de leverdatum."}
            </p>
          </div>
          {o.status === "bevestigd" && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-1.5" data-testid="order-bevestiging">
              <div className="flex items-center gap-1.5 text-sm font-medium text-emerald-800">
                <MailCheck className="h-4 w-4" /> Bevestigd door leverancier
              </div>
              {o.bevestigde_leverdatum && (
                <div className="text-sm text-emerald-800">Bevestigde leverdatum: <strong>{fmtDate(o.bevestigde_leverdatum)}</strong></div>
              )}
              {o.bevestiging_van && <div className="text-xs text-emerald-700">Van {o.bevestiging_van} · {fmtDate(o.bevestigd_op)}</div>}
              {o.bevestiging_tekst && (
                <details className="text-sm text-emerald-900">
                  <summary className="cursor-pointer">Bericht van leverancier</summary>
                  <pre className="whitespace-pre-wrap font-sans mt-1 text-sm">{o.bevestiging_tekst}</pre>
                </details>
              )}
            </div>
          )}
          <div className="pt-3 border-t border-border flex justify-between text-sm">
            <span className="text-muted-foreground">Aangemaakt</span>
            <span>{fmtDate(o.created_at)}</span>
          </div>
          {o.verzonden_op && (
            <div className="text-sm flex justify-between">
              <span className="text-muted-foreground">Verzonden op</span>
              <span>{fmtDate(o.verzonden_op)}</span>
            </div>
          )}
          {ingeboekt && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm space-y-1" data-testid="ingeboekt-info">
              <div className="flex items-center gap-1.5 font-medium text-emerald-700">
                <PackageCheck className="h-4 w-4" /> Ingeboekt op eigen warehouse
              </div>
              <div className="flex justify-between text-emerald-800/80 text-xs">
                <span>Door</span><span>{o.ingeboekt_door || "—"}</span>
              </div>
              <div className="flex justify-between text-emerald-800/80 text-xs">
                <span>Datum</span><span>{fmtDate(o.ingeboekt_op)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="bg-card border border-border rounded-xl p-4 flex items-center justify-between gap-3">
            <div>
              <Label>Artikelen</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Vink producten aan en stel aantal + inkoopprijs in.</p>
            </div>
            <ArtikelenInkoopDialog
              producten={producten}
              regels={o.regels || []}
              onSave={async (next) => { await setRegels(next); toast.success("Artikelen opgeslagen"); }}
              trigger={<Button data-testid="open-artikelen-dialog"><Plus className="h-4 w-4 mr-1" /> Artikelen beheren</Button>}
            />
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Art.nr. lev.</th>
                  <th className="text-left p-3">SKU</th>
                  <th className="text-left p-3">Naam</th>
                  <th className="text-right p-3">Aantal</th>
                  <th className="text-right p-3">Inkoop</th>
                  <th className="text-right p-3">Subtotaal</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(o.regels || []).map((r, idx) => (
                  <tr key={`${r.product_id}-${idx}`} data-testid={`order-regel-${idx}`}>
                    <td className="p-2">
                      <Input className="h-8" defaultValue={r.artikelcode_leverancier || ""}
                        onBlur={(e)=>{ if (e.target.value !== (r.artikelcode_leverancier || "")) updRegel(idx, "artikelcode_leverancier", e.target.value); }} />
                    </td>
                    <td className="p-2 font-mono text-xs">{r.code}</td>
                    <td className="p-2">{r.naam}</td>
                    <td className="p-2 text-right">
                      <Input className="h-8 text-right w-20 ml-auto" type="number" min="0" step="1"
                        defaultValue={r.aantal}
                        onBlur={(e)=>updRegel(idx, "aantal", parseFloat(e.target.value) || 0)} />
                    </td>
                    <td className="p-2 text-right">
                      <Input className="h-8 text-right w-24 ml-auto" type="number" min="0" step="0.01"
                        defaultValue={r.inkoopprijs}
                        onBlur={(e)=>updRegel(idx, "inkoopprijs", parseFloat(String(e.target.value).replace(",", ".")) || 0)} />
                    </td>
                    <td className="p-2 text-right font-medium">{fmtEUR((r.aantal||0)*(r.inkoopprijs||0))}</td>
                    <td className="p-2 text-right">
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={()=>delRegel(idx)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {(o.regels || []).length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Voeg artikelen toe via 'Artikelen beheren' hierboven.</td></tr>
                )}
              </tbody>
              <tfoot className="bg-secondary/30">
                <tr><td colSpan={5} className="p-3 text-right font-medium">Totaal</td>
                  <td className="p-3 text-right font-display text-lg">{fmtEUR(totaal)}</td><td></td></tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {(o.inboekingen || []).length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="inboek-historie">
          <h3 className="font-display text-xl font-medium mb-1">Ontvangsten ({o.inboekingen.length})</h3>
          <p className="text-xs text-muted-foreground mb-4">
            Alle (deel)leveringen van deze inkooporder. Elke ontvangst is als voorraadmutatie vastgelegd.
          </p>
          <div className="space-y-3">
            {[...o.inboekingen].reverse().map((b, i) => (
              <div key={b.id || i} className="border border-border rounded-lg p-3 text-sm"
                data-testid={`inboeking-${i}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="font-medium">
                    {fmtDate(b.datum)} — {b.stuks} stuks bij {b.bestemming === "warehouse" ? "eigen warehouse" : "leverancier"}
                  </div>
                  <div className="text-xs text-muted-foreground">{b.door}</div>
                </div>
                {b.notitie && <div className="text-xs text-muted-foreground mt-1">“{b.notitie}”</div>}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {(b.regels || []).map((r, ri) => (
                    <span key={ri}><span className="font-mono">{r.code}</span> +{r.aantal}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      <InboundSectie
        compact
        inkooporderId={id}
        voorafRef={o.nummer || ""}
        voorafRegels={(o.regels || []).filter(r => Number(r.aantal) > 0)
          .map(r => ({ sku: r.code, aantal: Number(r.aantal) }))}
        onKlaar={load}
      />

      <Dialog open={inboekOpen} onOpenChange={setInboekOpen}>
        <DialogContent className="max-w-2xl" data-testid="inboek-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" /> Voorraad inboeken
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Binnengekomen bij</Label>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={inboekBestemming === "leverancier" ? "default" : "outline"}
                onClick={() => setInboekBestemming("leverancier")} data-testid="inboek-bestemming-leverancier">
                Leverancier (order fabrikant aangekomen)
              </Button>
              <Button type="button" size="sm" variant={inboekBestemming === "warehouse" ? "default" : "outline"}
                onClick={() => setInboekBestemming("warehouse")} data-testid="inboek-bestemming-warehouse">
                Eigen warehouse
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Vul per artikel het nu ontvangen aantal in — deelleveringen kunnen in meerdere rondes
            worden ingeboekt (bijv. een ingevlogen deel). Bij bevestigen wordt dit{" "}
            {inboekBestemming === "warehouse"
              ? "van de leveranciersvoorraad naar het eigen warehouse verplaatst"
              : "van de geplaatste order bij de fabrikant naar de leveranciersvoorraad verplaatst"}
            {" "}en als voorraadmutatie vastgelegd (zichtbaar als paarse piek in de voorraadgrafiek).
          </p>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Aantekening bij deze levering (optioneel)</Label>
            <Input value={inboekNotitie} onChange={(e) => setInboekNotitie(e.target.value)}
              placeholder="Bijv. ingevlogen deel" className="h-9" data-testid="inboek-notitie" />
          </div>
          <div className="border border-border rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground sticky top-0">
                <tr>
                  <th className="text-left p-2">Artikel</th>
                  <th className="text-right p-2">Besteld</th>
                  <th className="text-right p-2">Al ontvangen</th>
                  <th className="text-right p-2">Nu ontvangen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {inboekRegels.map((r, idx) => (
                  <tr key={`${r.product_id}-${idx}`} data-testid={`inboek-regel-${idx}`}>
                    <td className="p-2">
                      <div className="font-medium">{r.naam}</div>
                      <div className="font-mono text-xs text-muted-foreground">{r.code}</div>
                      {!r.product_id && <div className="text-[10px] text-amber-600">geen product gekoppeld — geen voorraadmutatie</div>}
                    </td>
                    <td className="p-2 text-right text-muted-foreground">{r.besteld}</td>
                    <td className="p-2 text-right text-muted-foreground" data-testid={`inboek-al-ontvangen-${idx}`}>{r.alOntvangen}</td>
                    <td className="p-2 text-right">
                      <Input className="h-8 text-right w-24 ml-auto" type="number" min="0" step="1"
                        max={Math.max(0, r.besteld - r.alOntvangen)}
                        value={r.ontvangen}
                        onChange={(e) => setOntvangen(idx, e.target.value)}
                        data-testid={`inboek-ontvangen-${idx}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(() => {
            const restant = inboekRegels.reduce(
              (s, r) => s + Math.max(0, (r.besteld || 0) - (r.alOntvangen || 0) - (Number(r.ontvangen) || 0)), 0);
            if (restant <= 0) {
              return <p className="text-xs text-emerald-700">Hiermee is de volledige order ontvangen.</p>;
            }
            const gepland = restantPlan.reduce((s, r) => s + (Number(r.aantal) || 0), 0);
            return (
              <div className="space-y-2" data-testid="restant-planning">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">
                    Verwachte levering van het restant ({restant} stuks nog open)
                  </Label>
                  <Button type="button" size="sm" variant="ghost"
                    onClick={() => updatePlan([...restantPlan, { aantal: 0, datum: "", auto: false }])}
                    data-testid="restant-plan-toevoegen">
                    <Plus className="h-4 w-4 mr-1" /> Levering toevoegen
                  </Button>
                </div>
                {restantPlan.map((r, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`restant-plan-${i}`}>
                    <Input type="number" min="0" step="1" className="h-9 w-28" value={r.aantal}
                      onChange={(e) => updatePlan(restantPlan.map((x, xi) => xi === i
                        ? { ...x, aantal: Math.max(0, parseInt(e.target.value, 10) || 0), auto: false } : x))}
                      data-testid={`restant-plan-aantal-${i}`} />
                    <span className="text-xs text-muted-foreground">stuks verwacht op</span>
                    <Input type="date" className="h-9 w-44" value={r.datum}
                      onChange={(e) => setRestantPlan((p) => p.map((x, xi) => xi === i
                        ? { ...x, datum: e.target.value } : x))}
                      data-testid={`restant-plan-datum-${i}`} />
                    {restantPlan.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive"
                        onClick={() => updatePlan(restantPlan.filter((_, xi) => xi !== i))}
                        data-testid={`restant-plan-verwijder-${i}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  De eerst verwachte datum wordt de nieuwe leverdatum van deze inkooporder én de verwachte
                  binnenkomst op de betrokken producten. Gepland: {gepland} van {restant} stuks.
                </p>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInboekOpen(false)} data-testid="inboek-annuleer-btn">Annuleren</Button>
            <Button onClick={bevestigInboek} disabled={inboekBusy} data-testid="inboek-bevestig-btn">
              <PackageCheck className="h-4 w-4 mr-1" /> {inboekBusy ? "Bezig…" : "Bevestig inboeken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
